<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    :max-width="380"
    :min-width="380"
    location="end bottom"
    origin="end top"
    transition="scale-transition"
    :z-index="10000"
    :offset="8"
    eager
  >
    <template #activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-form ref="form" @submit.prevent="formSubmit" autocomplete="off" :disabled="formDisabled">
      <v-card elevation="2" border>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              {{ $t('menus.topBars.searchIn') }}:
            </v-col>
          </v-row>
          <v-container fluid class="ma-0 pa-0">
            <v-radio-group
              v-model="searchZone"
              hide-details
              class="my-4 pt-0"
              inline
            >
              <div class="d-flex" style="width: 100%;">
                <v-radio
                  :label="$t('menus.topBars.filesAndFolders')"
                  value="files"
                  style="width: 50%;"
                  density="compact"
                ></v-radio>
                <v-radio
                  :label="$t('menus.topBars.comments')"
                  value="comments"
                  style="width: 50%;"
                  density="compact"
                ></v-radio>
              </div>

              <div class="d-flex" style="width: 100%;">
                <v-radio
                  :label="$t('menus.topBars.subtitles')"
                  value="transcripts"
                  style="width: 50%;"
                  density="compact"
                />
                <v-radio
                  :label="$t('menus.topBars.labels')"
                  value="labels"
                  style="width: 50%;"
                  density="compact"
                />
              </div>
            </v-radio-group>

            <v-combobox
              v-if="isFileSearch"
              v-model="searchStringValue"
              :items="previousSearchQueries"
              :rules="searchStringRules"
              :label="$t('menus.topBars.searchText')"
              append-inner-icon="mdi-magnify"
              class="mb-2"
              density="compact"
              variant="solo-filled"
              flat
              single-line
              hide-no-data
              hide-details="auto"
            />

            <div v-if="isLabelSearch">
              <v-text-field
                v-model="searchLabel"
                :label="$t('menus.topBars.searchLabels')"
                variant="solo-filled"
                density="compact"
                hide-details
                clearable
                clear-icon="mdi-close-circle-outline"
                class="mb-4"
              ></v-text-field>

              <div v-if="isLoading" class="d-flex justify-center py-4">
                <v-progress-circular indeterminate color="primary" :aria-label="$t('common.loading')"></v-progress-circular>
              </div>

              <v-treeview
                v-else
                v-model:activated="searchLabelValue"
                :items="treeItems"
                :search="searchLabel"
                :filter="filterTree"
                item-value="id"
                item-title="name"
                item-children="children"
                density="compact"
                activatable
                return-object
                class="max-height-300 overflow-y-auto"
                selection-type="leaf"
                @update:activated="handleSelection"
              >
                <template v-slot:toggle="{ props, isOpen }">
                  <v-btn
                    v-bind="props"
                    variant="text"
                    density="comfortable"
                    :icon="isOpen ? '$treeviewCollapse' : '$treeviewExpand'"
                    :aria-label="isOpen ? $t('common.collapse') : $t('common.expand')"
                    size="32"
                  />
                </template>

                <template v-slot:prepend="{ item }">
                  <v-icon v-if="item.children" :color="item.children.length ? 'primary' : ''">
                    {{ item.open ? 'mdi-folder-open' : 'mdi-folder' }}
                  </v-icon>
                  <v-icon v-else color="grey">
                    mdi-label
                  </v-icon>
                </template>

                <template v-slot:label="{ item }">
                  <v-tooltip location="bottom" :aria-label="item.name">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" :class="{ 'font-weight-bold': item.children }">{{ item.name }}</span>
                    </template>

                    <span :class="{ 'font-weight-bold': item.children }">{{ item.name }}</span>
                  </v-tooltip>
                </template>
              </v-treeview>
            </div>

            <v-divider aria-hidden="true" />

            <v-checkbox
              v-model="wholeWordsOnly"
              :label="$t('menus.topBars.wholeWords')"
              class="shrink mr-0 mt-0"
              hide-details
              density="compact"
            />

            <v-checkbox
              v-model="allWords"
              :label="$t('menus.topBars.allWords')"
              class="shrink mr-0 mt-0"
              hide-details
              density="compact"
            />

            <v-checkbox
              hide-details
              class="shrink mr-0 mt-0"
              v-model="includeSubfolders"
              :label="$t('menus.topBars.includeSubfolders')"
              density="compact"
            />

            <v-checkbox
              hide-details
              class="shrink mr-0 mt-0"
              v-model="includeAssets"
              :label="$t('menus.topBars.includeAssets')"
              density="compact"
            />
          </v-container>

          <div v-if="errorMessage" class="bg-error">
            {{ errorMessage }}
          </div>

          <div v-if="!errorMessage && !currentNodeId" class="bg-error">
            {{ $t('menus.topBars.noActiveNode') }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn type="submit" :disabled="formDisabled || (isFileSearch && (searchQuery.length === 0 || searchQuery.length > 256))" color="primary"
                 elevation="0" block variant="flat">
            {{ $t('menus.topBars.search') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-menu>
</template>

<script>
import { useNodeStore } from '@/stores/NodeStore.js';
import { useSearchStore } from '@/stores/SearchStore.js';
import { useLabelStore } from '@/stores/LabelStore.js';

export default {
  name: "SearchForm",
  data() {
    return {
      searchZoneValues: ['files', 'comments', 'transcripts', 'labels'],
      searchZoneLabels: [this.$t('menus.topBars.filesAndFolders'), this.$t('menus.topBars.metadataAndLabels')],
      menu: false,
      searchZone: "files",
      isFileSearch: true,
      isLabelSearch: false,

      labelItems: [],
      labelItemsNodeId: null,
      isLoading: false,

      wholeWordsOnly: false,
      allWords: false,
      includeSubfolders: true,
      includeAssets: true,
      errorMessage: "",

      searchLabelValue: [],
      searchStringValue: "",
      searchStringRules: [
        (v) => !!v || this.$t('menus.topBars.searchStrIsRequired'),
        (v) => v.length <= 256 || this.$t('routes.search.queryTooLong'),
      ],

      timeoutId: null,
      nodeWithoutLabels: new Map(),
      treeItems: [],
      searchLabel: null,
    };
  },
  watch: {
    searchZone() {
      this.isFileSearch = this.searchZone !== 'labels';
      this.isLabelSearch = this.searchZone === 'labels';

      if (this.isFileSearch) {
        if (this.timeoutId) {
          this.isLoading = false;

          clearTimeout(this.timeoutId);
        }
      }

      if (this.isLabelSearch) {
        if (this.nodeWithoutLabels.has(this.currentNodeId)) {
          const timestamp = this.nodeWithoutLabels.get(this.currentNodeId);
          const now = new Date().getTime();
          if ((now - timestamp) < 60000) { // 1 minute
            return;
          }
        }

        if (this.labelItemsNodeId !== this.currentNodeId) {
          this.labelItems = [];
        }

        if (this.labelItems.length === 0) {
          this.isLoading = true;

          this.timeoutId = setTimeout(() => {
            this.loadLabelItems();
            this.timeoutId = null;
          }, 1500);
        }
      }
    },
    currentNodeId(newValue) {
      if (this.labelItemsNodeId) {
        const isLabelSearch = this.searchZone === 'labels';
        if (isLabelSearch && this.labelItemsNodeId !== newValue) {
          this.labelItems = [];
          this.searchZone = 'files';
          this.labelItemsNodeId = null;
        }
      }
    }
  },
  methods: {
    handleSelection(items) {
      if (items.length) {
        this.searchLabelValue = items.filter((item) => !item.children);
      } else {
        this.searchLabelValue = [];
      }
    },
    generateUniqueId() {
      return 'id-' + Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    },
    loadLabelItems() {
      const labelStore = useLabelStore();
      labelStore.findLabels({
        nodeId: this.currentNodeId,
      })
        .then((results) => {
          this.labelItemsNodeId = this.currentNodeId;

          if (Array.isArray(results)) {
            this.prepareLabelGroups(results);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    filterTree(item, search) {
      if (!search || !item) return true;

      const searchLower = search.toLowerCase().trim();

      // Check if item has name property
      if (!item.name) return false;

      const nameLower = item.name.toLowerCase();

      // Direct match
      return nameLower.includes(searchLower);
    },
    async formSubmit() {
      this.errorMessage = "";

      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.isLoading = false;
      }

      const { valid } = await this.$refs.form.validate();
      if (!valid) {
        return;
      }

      let searchValue = this.searchStringValue.trim();
      if (this.searchZone === 'labels') {
        if (this.searchLabelValue && this.searchLabelValue.length) {
          // Use selected labels if available
          const labelNames = this.searchLabelValue
            .filter((item) => !item.children) // Only include actual labels, not categories
            .map((item) => item.name);

          if (labelNames.length) {
            searchValue = labelNames.join('||');
          } else if (this.searchLabel) {
            searchValue = this.searchLabel.trim();
          } else {
            this.errorMessage = this.$t('menus.topBars.labelNotSelected');
            return;
          }
        } else if (this.searchLabel) {
          // Use search text if no selections
          searchValue = this.searchLabel.trim();
        } else {
          this.errorMessage = this.$t('menus.topBars.labelNotSelected');
          return;
        }
      }

      const searchStore = useSearchStore();
      searchStore._setSearchQuery(searchValue);

      const formData = {
        nodeid: this.currentNodeId,
        searchStr: searchValue,
        searchZone: this.searchZone,
        wholeWords: this.wholeWordsOnly ? 1 : 0,
        allWords: this.allWords ? 1 : 0,
        includeSubfolders: this.includeSubfolders ? 1 : 0,
        includeAssets: this.includeAssets ? 1 : 0,
      };

      const self = this;
      searchStore.search(formData)
        .then(() => {
          self.menu = false;
          if (self.$route.path !== "/search") {
            self.$router.push({ path: "/search" });
          }
        })
        .catch((error) => {
          if (error && error.response && error.response.data && error.response.data.errors && error.response.data.errors[0] && error.response.data.errors[0].issue) {
            if (error.response.data.errors[0].property === 'q') {
              if (error.response.data.errors[0].errorCode) {
                self.errorMessage = self.$replaceT(error.response.data.errors[0].errorCode);
              } else {
                self.errorMessage = self.$t('routes.search.queryTooLong');
              }
            } else {
              self.errorMessage = error.response.data.errors[0].issue;
            }
          } else if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
            if (error.response.data.error.code) {
              self.errorMessage = self.$replaceT(error.response.data.error.code);
            } else {
              self.errorMessage = error.response.data.error.message;
            }
          } else {
            self.errorMessage = "Error!";
            console.log(error);   // unhandled error
          }
        });
    },
    prepareLabelGroups(results) {
      const groupsMap = new Map();

      const mlLabelsCategoryId = this.generateUniqueId();
      const mlTextCategoryId = this.generateUniqueId();

      const aiCategories = new Map([
        [
          mlLabelsCategoryId,
          {
            id: mlLabelsCategoryId,
            name: this.$t('menus.topBars.labels'),
            children: []
          }
        ],
        [
          mlTextCategoryId,
          {
            id: mlTextCategoryId,
            name: this.$t('menus.topBars.text'),
            children: []
          }
        ]
      ]);

      // Create root categories
      groupsMap.set('ai', {
        id: 'ai',
        name: this.$t('menus.topBars.aiGenerated'),
        children: [],
      });

      groupsMap.set('predefined', {
        id: 'predefined',
        name: this.$t('menus.topBars.predefined'),
        children: []
      });

      groupsMap.set('custom', {
        id: 'custom',
        name: this.$t('menus.topBars.custom'),
        children: []
      });

      // Temporary storage for predefined categories
      const predefinedCategoriesMap = new Map();

      // Process results
      for (const item of results) {
        const label = {
          id: this.generateUniqueId(),
          name: item.content
        };

        if (item.type === 'ai' && item.category === 'mllabel') {
          // Add to AI Generated -> Labels
          aiCategories.get(mlLabelsCategoryId).children.push(label);
        } else if (item.type === 'ai' && item.category === 'mltext') {
          // Add to AI Generated -> Text
          aiCategories.get(mlTextCategoryId).children.push(label);
        } else if (item.type === 'predefined') {
          // Items with type 'predefined' are organized by their category property
          const categoryName = item.category;
          const categoryId = categoryName.toLowerCase().replace(/\s/g, '_');

          // Create category if it doesn't exist yet
          if (!predefinedCategoriesMap.has(categoryId)) {
            predefinedCategoriesMap.set(categoryId, {
              id: this.generateUniqueId(),
              name: categoryName,
              children: []
            });
          }

          // Add label to appropriate category
          predefinedCategoriesMap.get(categoryId).children.push(label);
        } else if (item.type === 'custom') {
          // Items with type 'custom' go to Custom group
          groupsMap.get('custom').children.push(label);
        } else {
          // Handle any other cases by putting them in custom
          groupsMap.get('custom').children.push(label);
        }
      }

      // Add all predefined categories to the predefined namespace
      if (predefinedCategoriesMap.size > 0) {
        const predefinedRoot = groupsMap.get('predefined');

        // Sort the predefined categories alphabetically by name
        const sortedPredefinedCategories = Array.from(predefinedCategoriesMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));

        // Sort the labels within each category
        sortedPredefinedCategories.forEach((category) => {
          category.children.sort((a, b) => a.name.localeCompare(b.name));
        });

        predefinedRoot.children = sortedPredefinedCategories;
      }

      groupsMap.get('ai').children = Array.from(aiCategories.values());

      // Sort labels in AI subcategories and Custom categories
      const aiCategory = groupsMap.get('ai');
      aiCategory.children.forEach((subcategory) => {
        subcategory.children.sort((a, b) => a.name.localeCompare(b.name));
      });

      const customCategory = groupsMap.get('custom');
      customCategory.children.sort((a, b) => a.name.localeCompare(b.name));

      const treeItems = [];

      // Only add categories that have children or subcategories with children
      for (const [groupId, group] of groupsMap.entries()) {
        // For the AI category, we need to check if any subcategories have children
        if (groupId === 'ai') {
          const hasChildrenInSubcategories = group.children.some((subcategory) => subcategory.children.length > 0);
          if (hasChildrenInSubcategories) {
            // Only keep subcategories that have children
            group.children = group.children.filter((subcategory) => subcategory.children.length > 0);
            treeItems.push(group);
          }
        } else if (group.children.length > 0) {
          treeItems.push(group);
        }
      }

      // Sort the main categories (AI Generated first, then Predefined, then Custom)
      this.treeItems = treeItems.sort((a, b) => {
        const order = { 'AI Generated': 1, 'Predefined': 2, 'Custom': 3 };
        return order[a.name] - order[b.name];
      });

      if (this.treeItems.length === 0) {
        const timestamp = new Date().getTime();
        this.nodeWithoutLabels.set(this.currentNodeId, timestamp);
      }
    }
  },
  computed: {
    currentNodeId() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode?.id || null;
    },
    formDisabled() {
      return !this.currentNodeId;
    },
    selectedLabels() {
      return this.searchLabelValue.filter((item) => !item.children);
    },
    previousSearchQueries() {
      const searchStore = useSearchStore();
      return searchStore.getPreviousSearchQueries;
    },
    searchQuery() {
      return this.searchStringValue?.trim() ?? '';
    },
  }
};
</script>

<style lang="scss" scoped>
.max-height-300 {
  max-height: 300px;
}
.overflow-y-auto {
  overflow-y: auto;
}
:deep(.v-treeview .v-list-item-action .v-btn--icon) {
  width: 28px !important;
  height: 28px !important;
  border-radius: 50% !important;
}
</style>
